/**
 * TiltLevel
 *
 * Vertical spirit-level UI. Reads beta from the Capacitor Motion plugin via the
 * global Context orientation and shows a ball inside a tall target rail:
 *   • ball moves UP / DOWN for betaErr = beta − 90° (forward/back tilt)
 *
 * We only care about beta (forward/back tilt), so the ball is constrained to
 * vertical travel and the beta error is amplified for clearer feedback.
 *
 * Tolerance (degrees):
 *   |beta − 90°| < BETA_TOL  → "level" (green)
 *
 * Props:
 *   onStable(isLevel)  — debounced 300 ms; parent transitions when true.
 */

import React, { useContext, useEffect, useRef, useState } from "react";
import { Context } from "../../../MyContext";

// Measured, not guessed: GeoCalib over 368 real sessions puts phone tilt at
// mean -0.85 deg, sigma 6.0, p5 -9.5, p95 +8.7 (sizewall-model
// pytorch/sampling_cfg.py and pitch_samples.py). The old 6.0 was exactly one
// sigma used as a hard limit, which turned away ~32% of natural placements --
// a phone propped against a cup falls over before it gets that upright.
// 9.0 accepts ~86% and covers the whole positive tail.
const BETA_TOL    = 9.0;  // degrees from vertical to enter "level" state
const RELEASE_TOL = 12.0; // degrees from vertical to exit  "level" state (hysteresis)

// Visual travel: how many px the ball moves per degree of error.
// Amplified vs. the old 2-D arena so small tilts are easy to read.
const BETA_SCALE  = 4;

// Tall rail dimensions. Half-height constrains the ball centre inside.
const ARENA_W = 96;  // px width of the rail
const ARENA_H = 240; // px height of the rail (tall — occupies more of the screen)
const HALF_H  = ARENA_H / 2;
const BALL    = 32;  // px diameter

interface TiltLevelProps {
  onStable: (isLevel: boolean) => void;
}

const TiltLevel: React.FC<TiltLevelProps> = ({ onStable }) => {
  const { orientation } = useContext(Context);
  const [isLevel, setIsLevel] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLevelRef  = useRef(false); // tracks committed state for hysteresis

  const betaErr = (orientation?.beta ?? 90) - 90; // 0 when perfectly vertical

  // Hysteresis: once level, keep it until the wider release tolerance is exceeded
  const level = isLevelRef.current
    ? Math.abs(betaErr) < RELEASE_TOL
    : Math.abs(betaErr) < BETA_TOL;

  // Ball position: vertical only, clamped to rail bounds
  const ballY = Math.max(-(HALF_H - BALL / 2), Math.min(HALF_H - BALL / 2, betaErr * BETA_SCALE));

  // Target zone height (px) matching the tolerance, amplified the same way
  const zoneH = BETA_TOL * BETA_SCALE * 2;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      isLevelRef.current = level;
      setIsLevel(level);
      onStable(level);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [level]); // eslint-disable-line react-hooks/exhaustive-deps

  const accent = isLevel ? "#4ade80" : "rgba(255,255,255,0.35)";

  return (
    <div className="flex flex-col items-center justify-center gap-5 select-none">
      {/* Instruction text */}
      <p className={`text-lg font-semibold tracking-wide transition-colors ${
        isLevel ? "text-green-400" : "text-white"
      }`}>
        {isLevel ? "Ready" : "Hold phone vertical"}
      </p>

      {/* Vertical rail */}
      <div
        style={{
          position: "relative",
          width:  ARENA_W,
          height: ARENA_H,
          borderRadius: 16,
          background: "rgba(255,255,255,0.07)",
          border: `2px solid ${accent}`,
          transition: "border-color 200ms",
          boxShadow: isLevel ? `0 0 20px 3px #4ade8044` : "none",
        }}
      >
        {/* Centre line */}
        <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 1, background: "rgba(255,255,255,0.12)", transform: "translateY(-50%)" }} />

        {/* Green target zone */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            top:  "50%",
            width:  "100%",
            height: zoneH,
            transform: "translate(-50%, -50%)",
            borderRadius: 6,
            border: `1.5px solid ${isLevel ? "#4ade8088" : "#ffffff22"}`,
            transition: "border-color 200ms",
          }}
        />

        {/* Ball */}
        <div
          style={{
            position: "absolute",
            width:  BALL,
            height: BALL,
            borderRadius: "50%",
            left: "50%",
            top:  "50%",
            transform: `translate(-50%, calc(-50% + ${ballY}px))`,
            transition: "transform 80ms linear, background 200ms",
            background: isLevel
              ? "radial-gradient(circle at 35% 35%, #86efac, #16a34a)"
              : "radial-gradient(circle at 35% 35%, #fca5a5, #dc2626)",
            boxShadow: isLevel ? "0 0 10px 3px #4ade80aa" : "0 0 6px 2px #f87171aa",
          }}
        />
      </div>

      {/* Numeric tilt hint (subtle) */}
      <p className="text-xs font-mono text-white/30">
        β {betaErr >= 0 ? "+" : ""}{betaErr.toFixed(1)}°
      </p>
    </div>
  );
};

export default TiltLevel;
